import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, Heart, Brain, Users, Leaf, ClipboardCheck } from "lucide-react";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import { IMAGES, SITE } from "@/lib/site";

const PILLARS = [
  { icon: Brain, title: "Mental Capacity", body: "Emotional regulation, resilience, and the inner skills to navigate life's challenges — individually and in groups." },
  { icon: Heart, title: "Practical Skills", body: "Cooking, daily routines, healthy living — the everyday capabilities that make independent life possible." },
  { icon: Leaf, title: "Community Participation", body: "Confidence to belong, connect and contribute — through outdoor activities, groups and shared experience." },
];

const STATS = [
  { value: "ACA", label: "Registered counsellor on staff" },
  { value: "1:6", label: "Facilitator to participant ratio" },
  { value: "100%", label: "Qualified, experienced team" },
  { value: "Mel", label: "Melbourne-based, community-led" },
];

const RIBBON = [
  "Holistic care", "Skill building", "Cooking groups", "Outdoor activities",
  "Counselling", "Justice engagement", "Case-manager partners", "Everyone can grow",
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden bg-warm-50">
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="River through forest"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-warm-50 via-warm-50/95 to-warm-50/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-warm-50/40 via-transparent to-white/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-24 pb-32 lg:pt-36 lg:pb-44">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-6">
              Community Services · Melbourne
            </p>
            <h1
              data-testid="hero-headline"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-ink-900"
            >
              Building the <em className="text-brand not-italic">skills</em> people need to live a better life.
            </h1>
            <p className="mt-7 text-lg lg:text-xl leading-relaxed text-ink-500 max-w-2xl">
              {SITE.name} is a Melbourne community services organisation. We work holistically with people
              to build the practical, social and emotional skills they need — through cooking groups, outdoor
              recreation, and counselling. We believe everyone has the capacity to grow.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/professionals"
                data-testid="hero-refer-cta"
                className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-sm font-semibold hover:bg-brand-hover transition-colors"
              >
                Refer a participant <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/program"
                data-testid="hero-program-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-ink-900 px-7 py-4 text-sm font-semibold hover:border-brand hover:text-brand transition-colors"
              >
                Explore our programs
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-ink-500">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand" /> ACA-registered counsellor</div>
              <div className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-brand" /> NDIS registered provider</div>
              <div className="hidden sm:flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-brand" /> Justice & private referrals welcome</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* RIBBON */}
      <div className="bg-warm-100 border-y border-warm-200/60 overflow-hidden">
        <div className="flex gap-12 py-6 animate-marquee whitespace-nowrap">
          {[...RIBBON, ...RIBBON, ...RIBBON].map((w, i) => (
            <span key={i} className="font-serif italic text-2xl text-ink-700/70">
              {w} <span className="mx-6 text-brand">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* PILLARS */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <Reveal>
            <SectionHeader
              kicker="Our approach"
              title="Three areas of skill. One person at a time."
              intro="A good life rests on a few core capacities: the practical skills to look after yourself, the social skills to belong, and the mental skills to navigate what comes. We work across all three — together."
            />
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div
                  data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="h-full rounded-2xl bg-warm-50 border border-warm-200/60 p-10 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/5 transition-all"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand mb-6">
                    <p.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-serif text-2xl text-ink-900 mb-3">{p.title}</h3>
                  <p className="text-ink-500 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SPLIT IMAGE */}
      <section className="py-20 lg:py-28 bg-warm-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <img
              src={IMAGES.group}
              alt="Group support session"
              className="w-full h-[460px] object-cover rounded-2xl"
            />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 lg:pl-8">
            <p className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-5">Built for case managers</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-ink-900">
              We engage the participants others find hardest to reach.
            </h2>
            <p className="mt-5 text-lg text-ink-500 leading-relaxed">
              Neurogility specialises in working with involuntary participants — people who arrive
              mandated, sceptical, or who have been resistant to support in the past. We also work
              alongside magistrates and case managers to support bail conditions and structured engagement
              for justice-involved participants.
            </p>
            <ul className="mt-8 space-y-3 text-ink-700">
              {[
                "Specialist engagement of involuntary & resistant participants",
                "Magistrate & justice system collaboration (bail conditions)",
                "Clear, regular communication with case managers",
                "Qualified, experienced facilitators across every program",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/professionals"
              data-testid="split-refer-cta"
              className="mt-10 inline-flex items-center gap-2 text-brand font-semibold hover:gap-3 transition-all"
            >
              Start a referral <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 lg:py-24 bg-ink-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <div data-testid={`stat-${i}`} className="border-t border-white/15 pt-6">
                <p className="font-serif text-5xl lg:text-6xl tracking-tight text-brand-soft">{s.value}</p>
                <p className="mt-3 text-sm text-white/70 leading-snug">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIAL TEASE */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-8">In their words</p>
            <blockquote className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-ink-900 tracking-tight">
              <span className="text-brand">"</span>They got through to a young man three previous services couldn't engage.
              That's the difference Neurogility makes.<span className="text-brand">"</span>
            </blockquote>
            <p className="mt-8 text-sm uppercase tracking-widest text-ink-400">— Sarah K., Senior Case Manager</p>
            <Link
              to="/ndis-services"
              data-testid="see-ndis-services"
              className="mt-10 inline-flex items-center gap-2 text-brand font-semibold hover:gap-3 transition-all"
            >
              Explore our NDIS services <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-warm-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-5">Refer with confidence</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-ink-900">
              Have a participant who needs the right kind of support?
              <br />
              <span className="italic text-brand">We're built for them.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/professionals"
              data-testid="closing-refer-cta"
              className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-sm font-semibold hover:bg-brand-hover transition-colors"
            >
              Begin a referral <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
